import { useCallback } from 'react';
import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery as useQueryInternal,
  QueryFunction,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDefaultRetryPolicy } from '@antimatterhq/ui/hooks/react-query/useDefaultRetryPolicy';
import { useDefaultRequestErrorHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestErrorHandling';
import { useDefaultRequestSuccessHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestSuccessHandling';

/**
 * @description A wrapper around `@tanstack/react-query`'s `useQuery` hook.
 * Provides enhancements:
 * 1.  **Automatic 401 Retry**: Automatically attempts to refresh the authentication token on a 401 error
 *     using `useDefaultRequestErrorHandling` and retries the query *once* if the refresh is successful.
 *     This retry is managed internally by throwing a specific `TokenRefreshSuccessError` and handling it
 *     in the custom `retry` logic.
 * 2.  **Default Retry Policy**: Applies a default retry strategy for *non-401* errors (e.g., network issues)
 *     using `useDefaultRetryPolicy`, unless a specific `retry` option is provided to the hook.
 * 3.  **Default Options**: Sets default values for `refetchOnWindowFocus` (false) and `refetchOnMount` (false)
 *     unless overridden in options.
 *
 * @template TQueryFnData - Type of data returned by the query function.
 * @template TError - Type of error thrown by the query function. Defaults to `AxiosError`.
 * @template TData - Type of data transformed/selected from TQueryFnData. Defaults to TQueryFnData.
 * @template TQueryKey - Type of the query key.
 *
 * @param {UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>} options - Original options passed to `useQuery`.
 * The `retry` option configures the *non-401* retry behavior.
 * The `throwOnError` option controls whether errors (excluding the internal 401 retry error) are thrown.
 * @returns {UseQueryResult<TData, TError>} The result object from `useQuery`.
 */
export function useQuery<
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> {
  const handleError = useDefaultRequestErrorHandling();
  const handleSuccess = useDefaultRequestSuccessHandling();
  const shouldRetry = useDefaultRetryPolicy();

  const queryFn: QueryFunction<TQueryFnData, TQueryKey, never> = useCallback(
    async (...args) => {
      try {
        const result = await (
          options.queryFn as QueryFunction<TQueryFnData, TQueryKey, never>
        )?.(...args);
        await handleSuccess();
        return result;
      } catch (error) {
        if (await handleError(error)) {
          // After error handling, try again in case the error was due to a 401.
          // If we made it this far, the token refresh worked and we can try again.
          // No try/catch here, we want to throw the error if it's not handled.
          const result = await (
            options.queryFn as QueryFunction<TQueryFnData, TQueryKey, never>
          )?.(...args);

          await handleSuccess();
          return result;
        }

        throw error;
      }
    },
    [options.queryFn, handleError, handleSuccess],
  );

  return useQueryInternal<TQueryFnData, TError, TData, TQueryKey>({
    ...options,
    queryFn,
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    refetchOnMount: options.refetchOnMount || false,
    retry: options.retry ?? shouldRetry,
    throwOnError: options.throwOnError ?? false,
  });
}
